import { useEffect } from 'react'

import { useCopilotChat } from '@copilotkit/react-core'
import { QuestionType } from '@factorialco/f0-react/dist/experimental'

import { useActivities } from 'modules/core/lib/activities/provider'
import i18n from 'modules/core/lib/i18n'

import { useAutoOpenChat } from 'modules/ai/hooks/useAutoOpenChat'
import { useCocreationAgent } from 'modules/ai_reports/lib/useCocreationAgent'
import { FormEditor } from 'modules/forms/components/newFormEditor/formEditor'

import { AgentStateType, QuestionnaireType } from './types'

export const FormWithAgent = ({
  initialState,
  savedState,
  reloadKey,
  setForm,
  allowedQuestionTypes,
  enabled,
}: {
  initialState: QuestionnaireType
  savedState: QuestionnaireType | undefined
  reloadKey: number
  setForm: (form: QuestionnaireType) => void
  allowedQuestionTypes?: QuestionType[]
  enabled?: boolean
}) => {
  const { isLoading } = useCopilotChat()
  const { state, setState } = useCocreationAgent<AgentStateType, AgentStateType>({
    name: 'co-creation-workflow',
    initialState: {
      questionnaire: initialState,
      updateForm: true,
    },
    initialMessage: i18n.t('forms.one.initial_message'),
    welcomeScreenSuggestions: [],
  })

  const { currentActivity } = useActivities()

  useAutoOpenChat({
    shouldAutoOpen: !currentActivity,
  })

  useEffect(() => {
    if (state?.questionnaire) {
      setForm(state.questionnaire)
    }
  }, [state, setForm])

  useEffect(() => {
    setForm(initialState)
  }, [initialState, setForm])

  useEffect(() => {
    if (savedState && reloadKey > 0) {
      setState?.({ ...state, questionnaire: savedState, updateForm: true })
    }
  }, [reloadKey])

  const handleChange = (questionnaire: QuestionnaireType) => {
    if (state) {
      setState?.({ ...state, questionnaire })
      setForm(questionnaire)
    }
  }

  if (!state) return null

  return (
    <FormEditor
      state={state.questionnaire}
      setState={handleChange}
      allowedQuestionTypes={allowedQuestionTypes}
      enabled={enabled}
      applyingChanges={isLoading}
    />
  )
}
